<template>
  <div class="Layout">
    <ux-atoms-typo :text="$t('pages.sitemap.sitemap')" as="h1" class="mt-6" color="dark" variant="expressive-heading-02" />
    <section v-if="restaurants?.length">
      <ux-atoms-typo :text="$t('pages.sitemap.findARestaurant')" as="h2" class="mt-7 mb-4" color="dark" variant="expressive-heading-04" />
      <ul class="Sitemap__list">
        <template :key="country?.code" v-for="country in countries">
          <li v-if="restaurantsInCountries(country?.code)?.length">
            <ux-atoms-typo :text="country?.name" as="h3" color="dark" variant="text-heading-01" />
            <ul class="mb-4" v-if="restaurantsInCountries(country?.code)">
              <li :key="`restaurant-in-country-${restaurant?.code}`" v-for="restaurant in restaurantsInCountries(country?.code)">
                <ux-atoms-link
                  :href="linkToRestaurant(restaurant.code)"
                  :text="restaurant?.name"
                  sitemap
                  underline
                  v-if="restaurant?.id && restaurant?.code"
                />
              </li>
            </ul>
          </li>
        </template>
      </ul>
    </section>
    <section v-if="cities?.length">
      <ux-atoms-typo :text="$t('pages.sitemap.topDestinations')" as="h2" class="mt-7 mb-4" color="dark" variant="expressive-heading-04" />
      <ul class="Sitemap__list">
        <li :key="`city-${city?.slug}`" v-for="city in cities">
          <ux-atoms-link :href="linkToCity(city)" :text="city?.name" sitemap underline v-if="city" />
        </li>
      </ul>
    </section>
    <section class="mb-6" v-if="inspiration">
      <ux-atoms-typo :text="$t('pages.sitemap.inspirationLinks')" as="h2" class="mt-7 mb-4" color="dark" variant="expressive-heading-04" />
      <ul class="Sitemap__list">
        <li>
          <ux-atoms-link :href="localePath('/inspiration')" :text="inspiration.title" sitemap underline />
        </li>
        <li :key="editorialContent?.slug ?? `editorialContent-${index}`" v-for="(editorialContent, index) in editorialContents">
          <ux-atoms-link
            :href="localePath(`/${editorialContent?.variant?.toLocaleLowerCase()}/${editorialContent?.slug}`)"
            :text="editorialContent?.title"
            sitemap
            underline
          />
        </li>
      </ul>
    </section>
    <section class="mb-6" v-if="linkToPersonalizedPage.length">
      <ux-atoms-typo :text="$t('pages.sitemap.usefulLinks')" as="h2" class="mt-7 mb-4" color="dark" variant="expressive-heading-04" />
      <ul class="Sitemap__list">
        <li :key="`page-${page?.slug}`" v-for="page in personalizedPages">
          <ux-atoms-link :href="linkToPersonalizedPage(page?.slug)" :text="page?.title" sitemap underline v-if="page?.slug" />
        </li>
      </ul>
    </section>
  </div>
</template>
<script lang="ts" setup>
import { City, useSitemapAsyncData } from '~/graphql';

const localePath = useLocalePath();
const { t } = useI18n();

const setI18nParams = useSetI18nParams();

setI18nParams({
  en: { sitemap: 'sitemap' },
  fr: { country: 'sitemap' }
});

const { data } = await useSitemapAsyncData();

const cities = computed(() => data.value?.sitemap?.cities);
const countries = computed(() => data.value?.sitemap?.countries);
const restaurants = computed(() => data.value?.sitemap?.restaurants);
const personalizedPages = computed(() => data.value?.sitemap?.personalizedPages);
const inspiration = computed(() => data.value?.sitemap?.inspiration);
const editorialContents = computed(() => data.value?.sitemap?.editorialContents);

const restaurantsInCountries = (countryCode: null | string | undefined) => {
  if (!countryCode) {
    return [];
  }
  return restaurants.value?.filter((restaurant) => restaurant?.countryCode === countryCode);
};

const linkToRestaurant = (restaurantCode: null | string) => {
  if (!restaurantCode) {
    return undefined;
  }

  return localePath({
    name: 'restaurant-restaurantCode',
    params: { restaurantCode }
  });
};

const linkToPersonalizedPage = (slug: string) => {
  return localePath({
    name: 'page-slug',
    params: { slug }
  });
};

const linkToCity = (city: City) => {
  return localePath({
    name: 'country-city-map',
    params: {
      city: city?.slug?.toLowerCase(),
      country: city?.countrySlug?.toLowerCase()
    }
  });
};

useHead(() => ({
  title: t('pages.sitemap.title')
}));
</script>
<style lang="scss" scoped>
@use '@styles/config/mq.scss';

.Sitemap__list {
  @include mq.media('>=medium') {
    column-count: 2;
  }

  @include mq.media('>=large') {
    column-count: 3;
  }
}
</style>
